import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { APP_API_URL } from '../../../utils';
import OrderLayout from '../../../layout/order-layout';
import UIDropdown from '../../../components/ui/ui-dropdown';
import UICustomerDropdown from '../../../components/ui/ui-customer-dropdown';
import Add from '../../customers/add';
import formatNumber, { calculateTaxItems } from '../../../utils/functions';

const SalesDamagedGoods = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [stock, setStock] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState({ label: 'Sélectionner un client', value: '' });
  const [cart, setCart] = useState([]);
  const [selectedPaiement, setSelectedPaiement] = useState({ label: 'Sélectionner un moyen', value: '' });
  const [referencePiece, setReferencePiece] = useState('');
  const [remisePercentage, setRemisePercentage] = useState(0);
  const [remiseFixed, setRemiseFixed] = useState(0);
  const [selectedAIBTax, setSelectedAIBTax] = useState({ label: 'AIB (0%)', value: null, emcf: null });
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const showReferencePiece = [3, 7].includes(selectedPaiement.value);

  const fetchStock = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/damaged-goods/stock`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStock(response.data.items || []);
    } catch (error) {
      console.error(error);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { per_page: 500, sort_by: 'name', sort_direction: 'asc' },
      });
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchStock();
    fetchCustomers();
  }, [fetchStock, fetchCustomers]);

  useEffect(() => {
    const saved = localStorage.getItem('damagedCart');
    if (saved) setCart(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem('damagedCart', JSON.stringify(cart));
  }, [cart]);

  const addItem = (item) => {
    setCart((prev) => {
      const existing = prev.find((line) => line.variantId === item.variant_id);
      if (existing) {
        if (existing.quantity + 1 > item.quantity) return prev;
        return prev.map((line) => (
          line.variantId === item.variant_id ? { ...line, quantity: line.quantity + 1 } : line
        ));
      }
      return [...prev, {
        uniqueId: Date.now(),
        variantId: item.variant_id,
        name: item.name,
        sku: item.sku,
        unit: item.unit || '',
        max: item.quantity,
        quantity: 1,
        customPrice: null,
      }];
    });
  };

  const calculateNetAmount = () => cart.reduce(
    (acc, line) => acc + (Number(line.customPrice) || 0) * (Number(line.quantity) || 0),
    0
  );
  const calculateRemise = () => remiseFixed + (remisePercentage / 100) * calculateNetAmount();
  const calculateTotal = () => calculateNetAmount() - calculateRemise();

  const canSubmit = selectedPaiement.value
    && calculateTotal() > 0
    && selectedCustomer?.value
    && cart.length > 0
    && cart.every((line) => line.customPrice > 0)
    && (!showReferencePiece || referencePiece)
    && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    const aibRate = selectedAIBTax.emcf === 'A' ? 0.01 : selectedAIBTax.emcf === 'B' ? 0.05 : null;
    const remise = calculateRemise();
    const taxItems = cart.map((line) => ({
      name: line.name,
      price: Number(line.customPrice),
      quantity: Number(line.quantity),
      taxGroup: 'B',
    }));
    try {
      const result = calculateTaxItems(taxItems, aibRate, remise);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${APP_API_URL}/damaged-goods/sell`, {
        items: cart.map((line) => ({
          variant_id: line.variantId,
          quantity: line.quantity,
          unit_price: line.customPrice,
        })),
        customer_id: selectedCustomer.value,
        payment_method: selectedPaiement.value,
        discount: remise,
        note: referencePiece,
        aib: result.totals.totalAIB,
        aib_value: selectedAIBTax.emcf ?? null,
        tva: result.totals.totalTVA,
        montant_ht: result.totals.montantHT,
        montant_net: result.totals.finalTTC,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ref = response.data.sale.id;
      setCart([]);
      localStorage.removeItem('damagedCart');
      await Swal.fire({ icon: 'success', title: 'Commande enregistrée', timer: 1400, showConfirmButton: false });
      navigate(`/admin/damaged-goods/validate/${ref}`);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        text: error.response?.data?.message || 'La commande n’a pas pu être enregistrée.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrderLayout>
      <div className="duo-caisse-layout">
        <div className="duo-caisse-products-col">
          <div className="duo-caisse-products-header">
            <div className="duo-caisse-toolbar">
              <Link to="/admin/damaged-goods/sales/list" className="duo-btn duo-btn--ghost duo-btn--sm">
                <i className="bi bi-list-ul" /> Historique
              </Link>
            </div>
            <h2 className="duo-caisse-title">Articles cassés / gâtés</h2>
            <p className="duo-caisse-subtitle">Le prix se saisit dans le panier. Le stock est retiré à la validation de la facture.</p>
          </div>
          <div className="duo-caisse-products-scroll">
            {catalogLoading ? (
              <p className="text-center text-muted py-5">Chargement du stock…</p>
            ) : stock.length === 0 ? (
              <div className="text-center py-5">
                <img src="/assets/img/no-product.png" width={240} height={240} alt="Aucun stock" />
                <p className="mt-3 text-muted">Aucun article cassé ou gâté à vendre.</p>
                <Link to="/admin/damaged-goods/create" className="duo-btn duo-btn--primary">Déclarer un article</Link>
              </div>
            ) : (
              <div className="duo-product-grid">
                {stock.map((item) => (
                  <div
                    key={item.variant_id}
                    role="button"
                    tabIndex={0}
                    className="duo-product-card"
                    onClick={() => addItem(item)}
                    onKeyDown={(e) => e.key === 'Enter' && addItem(item)}
                  >
                    <p className="duo-product-card__name">{item.name}</p>
                    <p className="duo-product-card__sku">{item.category || item.sku}</p>
                    <div className="duo-product-card__meta">
                      <span className="duo-product-card__stock">{item.quantity} {item.unit || ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="duo-caisse-cart-col">
          <div className="duo-cart-panel">
            <div className="duo-cart-panel__inner">
              <div className="duo-cart-panel__head">
                <h2>Commande <span className="duo-cart-count">{cart.length}</span></h2>
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost duo-btn--sm"
                  onClick={() => {
                    setCart([]);
                    localStorage.removeItem('damagedCart');
                  }}
                >
                  Vider
                </button>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="duo-form-label mb-0">Client</label>
                  <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => setShowAddCustomerModal(true)}>
                    Nouveau
                  </button>
                </div>
                <UICustomerDropdown
                  options={customers.map((item) => ({
                    label: item.name,
                    value: item.id,
                    phone: item.numero,
                    client_code: item.client_code,
                    email: item.email,
                    customer: item,
                  }))}
                  selectedOption={selectedCustomer}
                  onSelect={setSelectedCustomer}
                  enableSearch
                />
              </div>
              {cart.map((line) => (
                <div className="duo-cart-item" key={line.uniqueId}>
                  <div className="d-flex justify-content-between">
                    <span className="duo-cart-item__name">{line.name}</span>
                    <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => setCart(cart.filter((item) => item.uniqueId !== line.uniqueId))}>
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                  <div className="row g-2 mt-1">
                    <div className="col-6">
                      <label className="duo-form-label">Quantité {line.unit}</label>
                      <input
                        type="number"
                        className="duo-input"
                        min="0.01"
                        step="0.01"
                        max={line.max}
                        value={line.quantity}
                        onChange={(e) => {
                          let next = parseFloat(e.target.value);
                          if (!next || next < 0.01) next = 0.01;
                          if (next > line.max) next = line.max;
                          setCart(cart.map((item) => item.uniqueId === line.uniqueId ? { ...item, quantity: next } : item));
                        }}
                      />
                    </div>
                    <div className="col-6">
                      <label className="duo-form-label">Prix</label>
                      <input
                        type="number"
                        className="duo-input"
                        min="0"
                        value={line.customPrice ?? ''}
                        onChange={(e) => {
                          const price = parseFloat(e.target.value);
                          setCart(cart.map((item) => item.uniqueId === line.uniqueId ? { ...item, customPrice: Number.isNaN(price) ? null : price } : item));
                        }}
                      />
                    </div>
                  </div>
                  <p className="mb-0 mt-2" style={{ fontWeight: 800, color: '#006633' }}>
                    {formatNumber((line.customPrice || 0) * line.quantity)} FCFA
                  </p>
                </div>
              ))}
              <div className="duo-cart-totals mt-3">
                <label className="duo-form-label">AIB</label>
                <UIDropdown
                  options={[
                    { label: 'AIB (0%)', value: null, emcf: null },
                    { label: 'AIB (01%)', value: 0.01, emcf: 'A' },
                    { label: 'AIB (05%)', value: 0.05, emcf: 'B' },
                  ]}
                  selectedOption={selectedAIBTax}
                  onSelect={setSelectedAIBTax}
                />
                <div className="row g-2 mt-2">
                  <div className="col-6">
                    <label className="duo-form-label">Remise (%)</label>
                    <input type="number" className="duo-input" min="0" value={remisePercentage} onChange={(e) => setRemisePercentage(parseFloat(e.target.value) || 0)} />
                  </div>
                  <div className="col-6">
                    <label className="duo-form-label">Remise fixe</label>
                    <input type="number" className="duo-input" min="0" value={remiseFixed} onChange={(e) => setRemiseFixed(parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
                <p className="mt-2 mb-0 fw-bold">Total {formatNumber(calculateTotal())} FCFA</p>
              </div>
              <div className="mt-3">
                <label className="duo-form-label">Paiement</label>
                <UIDropdown
                  options={[
                    { label: 'Mobile Money', value: 1 },
                    { label: 'Carte bancaire', value: 2 },
                    { label: 'Virement', value: 3 },
                    { label: 'Espèces', value: 4 },
                    { label: 'Crédit', value: 5 },
                    { label: 'Chèques', value: 7 },
                    { label: 'Autre', value: 6 },
                  ]}
                  selectedOption={selectedPaiement}
                  onSelect={setSelectedPaiement}
                  enableSearch
                />
                {showReferencePiece && (
                  <input className="duo-input mt-2" placeholder="Référence pièce" value={referencePiece} onChange={(e) => setReferencePiece(e.target.value)} />
                )}
              </div>
              <button type="button" className="duo-btn duo-btn--primary w-100 mt-3" disabled={!canSubmit} onClick={handleSubmit}>
                {loading ? 'Validation…' : `Valider — ${formatNumber(calculateTotal())} FCFA`}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Add
        open={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onAddCustomer={(customer) => {
          setCustomers((prev) => [...prev, customer]);
          setSelectedCustomer({ label: customer.name, value: customer.id, customer });
        }}
        refreshData={fetchCustomers}
      />
    </OrderLayout>
  );
};

export default SalesDamagedGoods;
